import React, { useState, useRef, useEffect } from "react";
import { Menu, ChevronDown, LogOut, User, Search, Plus, Trello, ListChecks, Clock, Pause, Bell, Moon, Sun } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { can, roleLabel } from "../../lib/permissions";
import { NotificationBell } from "./NotificationBell";
import { Avatar } from "../ui/primitives";
import { useWorkTimer, formatDuration } from "../../hooks/useWorkTimer";
import { myAvatarUrl } from "../../api/profile";
import { api } from "../../lib/apiClient";
import { useToast } from "../../context/ToastContext";
import { boardPath } from "../../lib/boardPath";
import { menuItemForPath } from "../../lib/menuRegistry";
import { canAccessMenu } from "../../lib/menuAccess";
import clsx from "clsx";

const WorkTimerBadge: React.FC = () => {
  const { isRunning, todaySeconds, isLoading } = useWorkTimer();
  if (isLoading) return null;
  return (
    <span
      title={isRunning ? "Tracking your work time — keeps running across tabs and other apps, pauses only if this device sleeps or shuts down" : "Paused — resumes automatically once tracking picks back up"}
      className={clsx(
        "hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium sm:flex",
        isRunning ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
      )}
    >
      {isRunning ? <Clock className="h-3.5 w-3.5 animate-pulse" /> : <Pause className="h-3.5 w-3.5" />}
      {formatDuration(todaySeconds)} today
    </span>
  );
};

const BREADCRUMB_LABELS: Record<string, string> = {
  workflow: "Workflow",
  crm: "CRM",
  boards: "Projects",
  service: "Service",
  "my-tasks": "My Tasks",
  team: "Team Workload",
  "time-logs": "Time Logs",
  activity: "Recent Activity",
  settings: "Settings",
  profile: "My Profile",
  employees: "Employees",
  users: "Users",
  roles: "Roles & Permissions",
  organisation: "Organisation",
  tags: "Tags",
  notifications: "Notifications",
  audit: "Audit Trail",
  hrms: "HRMS",
  recruitment: "Recruitment",
  payroll: "Payroll",
  reports: "Reports",
  generate: "Notice Management",
  attendance: "Attendance",
  "labour-attendance": "Labour Attendance",
  "amc-work": "Emergency Work",
  calendar: "Calendar & Reminders",
  reminders: "My Reminders",
  upcoming: "Upcoming Events",
  "company-documents": "Company Documents",
  "air-tickets": "Air Tickets",
  "leave-summary": "Leave Summary",
  active: "Active Estimations",
  "follow-up": "Estimation Follow-up",
  amc: "AMC",
  dubai: "Dubai",
  "abu-dhabi": "Abu Dhabi",
  "employee-assets": "Employee Assets & Documents",
  erp: "ERP",
  finance: "Finance",
  owed: "Funds & Reimbursements",
  inventory: "Inventory",
  leave: "Request",
  tickets: "Support Tickets",
  customers: "Customers",
};

interface Crumb {
  label: string;
  to?: string;
}

/** Where each sidebar section starts, and the sidebar groups that have a page of their own. */
const MODULE_LANDING: Record<string, string> = {
  Workflow: "/workflow/enquiries",
  CRM: "/workflow/customers",
  HRMS: "/settings/employees",
  ERP: "/erp/overview",
  Finance: "/finance",
};
const GROUP_LANDING: Record<string, string> = {
  Recruitment: "/hrms/recruitment",
  "Company Documents": "/hrms/company-documents",
  Payroll: "/hrms/payroll",
  Reports: "/hrms/reports",
  "Accounts Receivable": "/finance/receivables",
};

/** The first crumb of each section links to where that section starts. */
const SECTION_LANDING: Record<string, string> = {
  workflow: "/workflow/enquiries",
  crm: "/workflow/customers",
  hrms: "/hrms/attendance",
  erp: "/erp/overview",
  finance: "/finance",
  settings: "/settings/profile",
};

/** Real list/landing pages a crumb may link to (detail pages and ids are never linked). */
const LINKABLE = new Set([
  "/workflow/boards",
  "/workflow/customers",
  "/workflow/procurement",
  "/hrms/recruitment",
  "/hrms/payroll",
  "/hrms/reports",
  "/finance",
  "/erp",
]);
// the employee profile page lives under /hrms but its list is in Settings
const LINK_OVERRIDES: Record<string, string> = { "/hrms/employees": "/settings/employees" };

const prettify = (s: string) => (BREADCRUMB_LABELS[s] ?? (/^[a-z-]+$/.test(s) ? s.replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase()) : s.length > 24 ? `${s.slice(0, 8)}…` : s));

function useBreadcrumbs(user: ReturnType<typeof useAuth>["user"]): Crumb[] {
  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  const accessible = (path: string) => {
    const item = menuItemForPath(path);
    return !item || canAccessMenu(user, item.key);
  };
  const linkFor = (path: string): string | undefined => {
    const target = LINK_OVERRIDES[path] ?? path;
    const isListPage = LINKABLE.has(target) || !!menuItemForPath(target)?.path && menuItemForPath(target)!.path === target;
    return isListPage && accessible(target) ? target : undefined;
  };

  // Quotation editor / previous quotations: they belong to a task on the Estimation board
  if (segments[0] === "workflow" && segments[1] === "tasks" && segments[3]?.startsWith("quotation")) {
    return [
      { label: "Workflow", to: accessible(SECTION_LANDING.workflow) ? SECTION_LANDING.workflow : undefined },
      { label: "Estimation", to: accessible("/workflow/estimation") ? "/workflow/estimation" : undefined },
      { label: segments[3] === "quotations" ? "Previous quotations" : "Quotation" },
    ];
  }
  // Any page that has a sidebar entry: Home / <sidebar section> / [<group with its own page>] / <page> / [detail…]
  const item = menuItemForPath(location.pathname);
  if (item) {
    const base = location.pathname.startsWith("/hrms/employees") ? "/hrms/employees" : item.path; // the employee profile is a child of Employees
    const trailing = location.pathname.slice(base.length).split("/").filter(Boolean);
    const parts = item.label.split(" · ");
    const pageLabel = parts[parts.length - 1];
    const group = parts.length > 1 ? parts[0] : null;
    const landing = MODULE_LANDING[item.module];
    const crumbs: Crumb[] = [{ label: item.module, to: landing && accessible(landing) ? landing : undefined }];
    if (group && GROUP_LANDING[group]) crumbs.push({ label: group, to: accessible(GROUP_LANDING[group]) ? GROUP_LANDING[group] : undefined });
    crumbs.push({ label: pageLabel, to: trailing.length ? item.path : undefined });
    trailing.forEach((seg) => crumbs.push({ label: prettify(seg) }));
    return crumbs;
  }

  const customers = segments[0] === "workflow" && segments[1] === "customers";
  return segments.map((seg, i) => {
    const last = i === segments.length - 1;
    const path = "/" + segments.slice(0, i + 1).join("/");
    // Customers lives under CRM in the sidebar even though its URL starts with /workflow/ — label it that way
    const label = i === 0 && customers ? "CRM" : prettify(seg);
    if (last) return { label };
    if (i === 0) {
      const landing = customers ? SECTION_LANDING.crm : SECTION_LANDING[seg];
      return { label, to: landing && accessible(landing) ? landing : undefined };
    }
    return { label, to: linkFor(path) };
  });
}

// A Task ID (WF-000001), Project ID (QPTS-PRJ-2026-0001), Claim ID
// (CLM-000001), Enquiry ID (QPTS-ENQ-2026-0001), Estimation ID
// (QPTS-2026-0001) or Quotation Ref (QPTS/QN/2026-0001) typed into the
// search box — searched for an exact match so it jumps straight to that
// record. Order matters: QPTS-PRJ- and QPTS-ENQ- must be checked before the
// bare QPTS-\d{4}- (Estimation ID) alternative, otherwise a shorter prefix
// match could misfire — hence the negative lookahead below. The trailing
// (-[A-Za-z0-9]+)* allows a bulk-imported quotation ref's revision suffix
// (e.g. "-R3", "-Rev2", "-DUP2") to still match.
const ID_LOOKUP_PATTERN = /^(WF-\d+|CLM-\d+|QPTS-PRJ-\d{4}-\d+|QPTS-ENQ-\d{4}-\d+|QPTS(?!-PRJ-|-ENQ-)-\d{4}-\d+|QPTS\/QN\/\d{4}-\d+(-[A-Za-z0-9]+)*)$/i;

export const Header: React.FC<{ onOpenMobileMenu: () => void }> = ({ onOpenMobileMenu }) => {
  const { user, logout } = useAuth();
  const { push } = useToast();
  const crumbs = useBreadcrumbs(user);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  // "" = paste a full ID as before; ENQ/EST/PRJ = pick the record type and
  // type only the last 4 digits — the current year fills in the rest, since
  // that's what almost every search is for.
  const [idPrefix, setIdPrefix] = useState<"" | "ENQ" | "EST" | "PRJ" | "MRF" | "LPO">("");
  const ref = useRef<HTMLDivElement>(null);
  const createRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
      if (createRef.current && !createRef.current.contains(e.target as Node)) setCreateOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  async function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;

    // request 1129: material request and LPO numbers (any LPO format, e.g. QPTS-LPO-372-R1)
    const lookupDoc = async (term: string, strict: boolean) => {
      setSearching(true);
      try {
        const { data } = await api.get("/id-lookup", { params: { q: term } });
        const hits = data.data.hits as Array<{ kind: string; label: string; sub: string; path: string }>;
        const exact = hits.filter((h) => h.label.replace(/\s+/g, "").toLowerCase() === term.replace(/\s+/g, "").toLowerCase());
        const pick = exact.length === 1 ? exact[0] : strict && hits.length === 1 ? hits[0] : null; // a plain search opens only an exact match
        if (pick) {
          setSearch("");
          navigate(pick.path);
          return true;
        }
        if (hits.length > 1 && (strict || exact.length > 1)) {
          push({ variant: "error", title: `${hits.length} matches`, description: `${hits.slice(0, 4).map((h) => `${h.label} (${h.sub})`).join(", ")}${hits.length > 4 ? "…" : ""} — type the full number.` });
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        setSearching(false);
      }
    };
    if (idPrefix === "MRF" || idPrefix === "LPO") {
      const digits = q.replace(/\D/g, "");
      if (!digits) return;
      const term = idPrefix === "MRF" ? `QPTS-MRF-${new Date().getFullYear()}-${digits.padStart(4, "0")}` : `LPO-${digits}`;
      if (!(await lookupDoc(term, true))) push({ variant: "error", title: "Not found", description: `No ${idPrefix === "MRF" ? "material request" : "LPO"} found for "${term}".` });
      return;
    }
    const looksLikeDoc = /MRF|LPO/i.test(q);
    if (looksLikeDoc) {
      if (!(await lookupDoc(q, true))) push({ variant: "error", title: "Not found", description: `No material request or LPO found for "${q}".` });
      return;
    }

    let compact: string;
    if (idPrefix) {
      // Prefix mode: the box only holds the last 4 digits — reconstruct the
      // full ID with the current year, then fall through to the exact same
      // lookup logic below as if that full ID had been pasted in directly.
      const digits = q.replace(/\D/g, "");
      if (!digits) return;
      const year = new Date().getFullYear();
      const seq = digits.padStart(4, "0");
      compact = idPrefix === "PRJ" ? `QPTS-PRJ-${year}-${seq}` : idPrefix === "ENQ" ? `QPTS-ENQ-${year}-${seq}` : `QPTS/QN/${year}-${seq}`;
    } else {
      // Collapse stray internal whitespace (e.g. a typo like "QPTS/QN/2026- 0006")
      // before matching against an ID pattern — none of these codes contain
      // meaningful spaces, unlike a project-name search.
      compact = q.replace(/\s+/g, "");
    }

    if (ID_LOOKUP_PATTERN.test(compact)) {
      setSearching(true);
      try {
        const upper = compact.toUpperCase();
        const isProjectId = upper.startsWith("QPTS-PRJ-");
        const isClaimId = upper.startsWith("CLM-");
        if (isProjectId) {
          const { data } = await api.get("/boards/search/lookup", { params: { q: compact } });
          const match = (data.data as any[]).find((b) => b.boardId.toLowerCase() === compact.toLowerCase());
          if (match) {
            setSearch("");
            // A Completed/Archived project no longer shows on the live
            // Projects page — it only lives in Project/Task History now, so
            // send the search there instead of a project board it can't
            // actually be found on.
            if (match.isCompleted || match.isArchived) navigate(`/workflow/history?highlight=${match.id}`);
            else navigate(boardPath(match.name, match.id));
            return;
          }
        } else if (isClaimId) {
          const { data } = await api.get("/claims/search/lookup", { params: { q: compact } });
          const match = data.data as { id: string; claimId: string; status: string } | null;
          if (match) {
            setSearch("");
            const tab = match.status === "SETTLED" ? "settlements" : "claim";
            navigate(`/hrms/leave?tab=${tab}&claim=${match.id}`);
            return;
          }
        } else {
          const { data } = await api.get("/tasks/search/lookup", { params: { q: compact } });
          const ql = compact.toLowerCase();
          const rows = data.data as any[];
          // request 1250: a quotation number also opens its revision ("QPTS/QN/2026-2566" → "…-2566-REV01"), the latest one if several
          const revisionOf = (v?: string | null) => !!v && v.toLowerCase().startsWith(`${ql}-`);
          const match =
            rows.find(
              (t) =>
                t.taskId.toLowerCase() === ql ||
                t.enquiryRecord?.enquiryId?.toLowerCase() === ql ||
                t.estimationRecord?.estimationId?.toLowerCase() === ql ||
                t.estimationRecord?.quotationRef?.toLowerCase() === ql
            ) ??
            (ql.startsWith("qpts/qn/")
              ? rows
                  .filter((t) => revisionOf(t.estimationRecord?.estimationId) || revisionOf(t.estimationRecord?.quotationRef))
                  .sort((a, b) => String(b.estimationRecord?.quotationRef ?? b.estimationRecord?.estimationId).localeCompare(String(a.estimationRecord?.quotationRef ?? a.estimationRecord?.estimationId)))[0]
              : undefined);
          if (match) {
            setSearch("");
            // Same reasoning as the project-id branch above — an Awarded
            // task whose project has since been marked Completed/Archived
            // is only reachable from Project/Task History now.
            if (match.board?.isCompleted || match.board?.isArchived) navigate(`/workflow/history?highlight=${match.boardId}`);
            else navigate(`${boardPath(match.board?.name, match.boardId)}?task=${match.id}`);
            return;
          }
        }
        const typeLabel = idPrefix === "PRJ" ? "project" : idPrefix === "ENQ" ? "enquiry" : idPrefix === "EST" ? "estimation" : isProjectId ? "project" : isClaimId ? "claim" : "task";
        push({
          variant: "error",
          title: "Not found",
          description: `No ${typeLabel} found for "${idPrefix ? compact : q}".`,
        });
      } catch {
        push({ variant: "error", title: "Search failed", description: "Could not look up that ID right now." });
      } finally {
        setSearching(false);
      }
      return;
    }

    // a custom LPO number that doesn't say "LPO" — opened only on an exact match, otherwise it's a project search
    if (q.length >= 3 && (await lookupDoc(q, false))) return;
    navigate(`/workflow/boards?search=${encodeURIComponent(q)}`);
  }

  const canCreateBoard = can(user, "CREATE_BOARD");
  const canCreateTask = can(user, "CREATE_TASK");
  const isStaff = user?.roles.includes("STAFF") ?? false;

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden" onClick={onOpenMobileMenu} aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </button>
        {/* request 1152: the breadcrumb gives way (its labels truncate) — the search box keeps its width. Request 1232: the search box claims its width first (it no longer gets only what the breadcrumb leaves over) */}
        <nav aria-label="Breadcrumb" className="hidden min-w-0 flex-[0_1_auto] items-center gap-1.5 overflow-hidden whitespace-nowrap text-sm text-slate-500 xl:flex">
          <Link to="/dashboard" className="shrink-0 hover:text-slate-700">
            Home
          </Link>
          {crumbs.map((c, i) => (
            <React.Fragment key={i}>
              <span className="shrink-0 text-slate-300">/</span>
              {c.to ? (
                <Link to={c.to} title={c.label} className={clsx("truncate hover:text-slate-800 hover:underline", i === crumbs.length - 1 ? "min-w-[3rem]" : "min-w-[2rem] max-w-[9rem]")}>
                  {c.label}
                </Link>
              ) : (
                <span title={c.label} className={clsx("truncate", i === crumbs.length - 1 ? "min-w-[3rem] font-medium text-slate-900" : "min-w-[2rem] max-w-[9rem]")}>
                  {c.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
        {/* request 1233: in dark mode the search field is a raised panel like in light mode, not darker than the header */}
        <form onSubmit={submitSearch} className="hidden min-w-[15rem] max-w-xl flex-1 items-center gap-1.5 sm:flex xl:flex-[1_0_24rem]">
          <select
            value={idPrefix}
            onChange={(e) => {
              setIdPrefix(e.target.value as "" | "ENQ" | "EST" | "PRJ" | "MRF" | "LPO");
              setSearch("");
              searchRef.current?.focus();
            }}
            aria-label="Search by ID type"
            className="w-[6.5rem] shrink-0 truncate rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-2 pr-6 text-xs text-slate-600 focus-visible:focus-ring dark:border-[#33415c] dark:bg-[#1c2639] dark:text-slate-200 dark:hover:border-[#475a7c]"
          >
            <option value="">Full ID</option>
            <option value="ENQ">Enquiry</option>
            <option value="EST">Estimation</option>
            <option value="PRJ">Project</option>
            <option value="MRF">Material Request</option>
            <option value="LPO">LPO</option>
          </select>
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(idPrefix ? e.target.value.replace(/\D/g, "").slice(0, 4) : e.target.value)}
              disabled={searching}
              inputMode={idPrefix ? "numeric" : "text"}
              placeholder={
                idPrefix
                  ? idPrefix === "LPO"
                    ? "LPO number, e.g. 372"
                    : `Last 4 digits of this year's ${idPrefix === "PRJ" ? "Project" : idPrefix === "ENQ" ? "Enquiry" : idPrefix === "MRF" ? "Material Request" : "Estimation"} ID, e.g. 0007`
                  : "Search by Task, Project, Claim, Material Request (QPTS-MRF-…) or LPO number…"
              }
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-9 pr-14 text-sm text-slate-700 placeholder:text-slate-400 focus-visible:focus-ring disabled:opacity-60 dark:border-[#33415c] dark:bg-[#1c2639] dark:text-slate-100 dark:placeholder:text-slate-400 dark:hover:border-[#475a7c] dark:focus:bg-[#202c42]"
            />
            <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 whitespace-nowrap rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400 md:block dark:border-[#3b4a66] dark:bg-[#26324a] dark:text-slate-300">
              Ctrl K
            </kbd>
          </div>
        </form>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <WorkTimerBadge />
        {(canCreateBoard || canCreateTask) && (
          <div className="relative" ref={createRef}>
            <button
              onClick={() => setCreateOpen((o) => !o)}
              className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New</span>
              <ChevronDown className="h-3.5 w-3.5 opacity-80" />
            </button>
            {createOpen && (
              <div className="absolute right-0 z-30 mt-2 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-xl">
                {canCreateTask && (
                  <Link
                    to="/workflow/my-tasks?newTask=1"
                    onClick={() => setCreateOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
                  >
                    <ListChecks className="h-4 w-4" /> New Task
                  </Link>
                )}
                {canCreateBoard && (
                  <Link
                    to="/workflow/boards?newBoard=1"
                    onClick={() => setCreateOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
                  >
                    <Trello className="h-4 w-4" /> New Project
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
        <ThemeToggle />
        <NotificationBell />
        <div className="relative" ref={ref}>
          <button onClick={() => setMenuOpen((o) => !o)} className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-100">
            <Avatar name={user?.name ?? "?"} size="sm" src={user?.hasAvatar ? myAvatarUrl(user.avatarUpdatedAt ?? undefined) : undefined} />
            <span className="hidden text-left sm:block">
              <span className="block text-sm font-medium leading-tight text-slate-700">{user?.name}</span>
              <span className="block text-xs leading-tight text-slate-400">{user?.roles[0] ? roleLabel(user.roles[0]) : ""}</span>
            </span>
            <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:inline" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-30 mt-2 w-52 rounded-lg border border-slate-200 bg-white py-1 shadow-xl">
              <div className="border-b border-slate-100 px-3 py-2">
                <p className="truncate text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="truncate text-xs text-slate-500">{user?.workEmail}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wide text-brand-600">{user?.roles.map(roleLabel).join(", ")}</p>
              </div>
              <Link to="/settings/profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
                <User className="h-4 w-4" /> My Profile
              </Link>
              {!isStaff && (
                <Link to="/settings/notifications" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
                  <Bell className="h-4 w-4" /> Notification settings
                </Link>
              )}
              <button onClick={logout} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

/** Request 1222 — light / dark for the whole app, each person's own choice. */
function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      title={dark ? "Switch to light theme" : "Switch to dark theme"}
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
    >
      <Sun className={clsx("absolute h-[18px] w-[18px] text-amber-500 transition-all duration-500", dark ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100")} />
      <Moon className={clsx("absolute h-[18px] w-[18px] text-indigo-300 transition-all duration-500", dark ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-0 opacity-0")} />
    </button>
  );
}
